import { Dispatch } from "react";
import { Popover } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useModal } from "../Sliders/Modal/ModalContext";
import * as Actions from "../store/actions";
import { formatCurrency } from "../util/Helpers";

const ORANGE = "#FF6A1A";
const NAVY = "#101535";

// ── Reusable menu row ─────────────────────────────────────────────
const MenuItem = ({
  icon,
  label,
  onClick,
  danger = false,
}: {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) => (
  <div
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "9px 12px",
      borderRadius: 10,
      cursor: "pointer",
      color: danger ? "#EF4444" : NAVY,
      fontSize: 14,
      fontWeight: 500,
      transition: "background .15s",
    }}
    onMouseOver={(e) =>
      ((e.currentTarget as HTMLElement).style.background = danger
        ? "rgba(239,68,68,.07)"
        : "rgba(255,106,26,.07)")
    }
    onMouseOut={(e) =>
      ((e.currentTarget as HTMLElement).style.background = "transparent")
    }
  >
    <span style={{ color: danger ? "#EF4444" : "#9CA3AF", display: "flex", flexShrink: 0 }}>
      {icon}
    </span>
    {label}
  </div>
);

// ── Avatar component ───────────────────────────────────────────────
const Avatar = ({ setIsOpen2 }: any) => {
  const navigate = useNavigate();
  const dispatch: Dispatch<any> = useDispatch();
  const { showModal } = useModal();
  const isAuth = useSelector((state: any) => state.client.isAuth);
  const user = useSelector((state: any) => state.client.user);
  const currency = useSelector((state: any) => state.app.currency);
  const accountBalance = useSelector((state: any) => state.client.accountBalance);

  const initial = ((user?.fullname || user?.email || "U")[0] || "U").toUpperCase();
  const displayName =
    user?.fullname?.split(" ")[0] || user?.email?.split("@")[0] || "Account";

  // ── Not signed in ────────────────────────────────────────────────
  if (!isAuth) {
    return (
      <button
        type="button"
        onClick={() => showModal("authModal")}
        style={{
          background: ORANGE,
          color: "#fff",
          border: "none",
          borderRadius: 999,
          padding: "8px 22px",
          fontWeight: 700,
          fontSize: 14,
          cursor: "pointer",
          fontFamily: "inherit",
          boxShadow: "0 4px 14px -4px rgba(255,106,26,.65)",
          transition: "background .2s",
          whiteSpace: "nowrap",
          letterSpacing: ".1px",
        }}
        onMouseOver={(e) =>
          ((e.currentTarget as HTMLElement).style.background = "#E8550B")
        }
        onMouseOut={(e) =>
          ((e.currentTarget as HTMLElement).style.background = ORANGE)
        }
      >
        Sign in
      </button>
    );
  }

  // ── Signed in ────────────────────────────────────────────────────
  return (
    <div style={{ position: "relative", zIndex: 999 }}>
      <Popover>
        {({ close }) => (
          <>
            {/* Trigger pill */}
            <Popover.Button as="div" style={{ cursor: "pointer", userSelect: "none" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(255,255,255,.12)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,.22)",
                  borderRadius: 999,
                  padding: "4px 12px 4px 4px",
                  transition: "background .18s, border-color .18s",
                }}
                onMouseOver={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "rgba(255,255,255,.2)";
                  (e.currentTarget as HTMLElement).style.borderColor =
                    "rgba(255,255,255,.36)";
                }}
                onMouseOut={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "rgba(255,255,255,.12)";
                  (e.currentTarget as HTMLElement).style.borderColor =
                    "rgba(255,255,255,.22)";
                }}
              >
                {/* Avatar circle */}
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg,#FF6A1A,#E8550B)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {initial}
                </div>
                {/* First name */}
                <span
                  style={{
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    maxWidth: 90,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                </span>
                {/* Chevron */}
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(255,255,255,.65)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </Popover.Button>

            {/* Dropdown panel */}
            <Popover.Panel
              style={{
                position: "absolute",
                right: 0,
                top: "calc(100% + 10px)",
                width: 300,
                background: "rgba(252,250,247,0.96)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: "1px solid rgba(255,255,255,0.7)",
                borderRadius: 18,
                boxShadow:
                  "0 20px 50px -10px rgba(16,21,53,.28), 0 0 0 1px rgba(16,21,53,.04)",
                overflow: "hidden",
                zIndex: 999,
              }}
            >
              {/* ── User header ── */}
              <div
                style={{
                  padding: "16px",
                  borderBottom: "1px solid #EDE8E0",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                {/* Large avatar */}
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg,#FF6A1A,#E8550B)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 20,
                    flexShrink: 0,
                  }}
                >
                  {initial}
                </div>

                <div style={{ minWidth: 0, flex: 1 }}>
                  {user?.fullname && (
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: NAVY,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        marginBottom: 2,
                      }}
                    >
                      {user.fullname}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6C7088",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user?.email}
                  </div>
                  {currency && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsOpen2?.(true);
                        close();
                      }}
                      style={{
                        marginTop: 6,
                        background: "rgba(255,106,26,.1)",
                        border: "none",
                        borderRadius: 999,
                        padding: "2px 9px",
                        fontSize: 11,
                        fontWeight: 700,
                        color: ORANGE,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      {currency.code}
                      <svg
                        width="9"
                        height="9"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* ── Reward balance ── */}
              {accountBalance && (
                <div
                  style={{
                    padding: "10px 16px",
                    borderBottom: "1px solid #EDE8E0",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <img src="/coins.png" width={20} alt="coins" style={{ flexShrink: 0 }} />
                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: ".4px",
                        textTransform: "uppercase",
                        color: "#9CA3AF",
                        marginBottom: 2,
                      }}
                    >
                      Reward earned
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                      {formatCurrency(accountBalance, "USD")}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Referral link ── */}
              {user?.refCode && (
                <div
                  style={{
                    padding: "10px 16px",
                    borderBottom: "1px solid #EDE8E0",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: ".4px",
                      textTransform: "uppercase",
                      color: "#9CA3AF",
                      marginBottom: 4,
                    }}
                  >
                    Referral link
                  </div>
                  <a
                    href={`https://stavigo.com?refCode=${user.refCode}`}
                    style={{
                      fontSize: 12,
                      color: ORANGE,
                      fontWeight: 500,
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textDecoration: "none",
                    }}
                  >
                    stavigo.com?refCode={user.refCode}
                  </a>
                </div>
              )}

              {/* ── Nav items ── */}
              <div style={{ padding: "6px 8px" }}>
                <MenuItem
                  onClick={() => {
                    navigate("/profile#Profile");
                    close();
                  }}
                  label="Account"
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                    </svg>
                  }
                />
                <MenuItem
                  onClick={() => {
                    navigate("/profile#My%20Bookings");
                    close();
                  }}
                  label="My bookings"
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <rect x="3" y="4" width="18" height="13" rx="2" />
                      <path d="M3 9h18M7 21v-2M17 21v-2" />
                    </svg>
                  }
                />
              </div>

              {/* ── Sign out ── */}
              <div style={{ padding: "0 8px 8px", borderTop: "1px solid #EDE8E0" }}>
                <MenuItem
                  onClick={() => {
                    dispatch(Actions.logoutClient());
                    close();
                  }}
                  label="Sign out"
                  danger
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  }
                />
              </div>
            </Popover.Panel>
          </>
        )}
      </Popover>
    </div>
  );
};

export default Avatar;
